<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay, useTheme } from 'vuetify'
import { useMobileDialogProps } from '@/composables/useMobileDialogProps'
import { useReducedMotion } from '@/composables/useReducedMotion'

const props = defineProps<{
  modelValue: boolean
  persistent?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  finish: []
  dismiss: []
}>()

const step = ref(1)
const router = useRouter()
const theme = useTheme()
const { xs } = useDisplay()
const reducedMotion = useReducedMotion()
const titleId = useId()

const steps = [
  {
    title: 'Add your kids',
    icon: 'mdi-account-child',
    tint: 'blue',
    text: 'Add a child profile on the Family page, then generate a QR login code for their own device. Each kid gets their own code, with no email or password needed.',
  },
  {
    title: 'Pick chores, set the stars',
    icon: 'mdi-clipboard-check-outline',
    tint: 'yellow',
    text: 'On the Chores page, start from the library or make a one-off chore. Assign it to your child and set the points it is worth. Bigger jobs can earn more points!',
  },
  {
    title: 'Approve, and they cash in',
    icon: 'mdi-check-decagram',
    tint: 'green',
    text: 'Your child marks the chore done; you approve it on the Review page to award points they can spend on rewards. Replay this tour any time from Help & feedback in Settings.',
  },
]

// Controlled open state. Block programmatic close while persistent (first login);
// only finish() may close in that mode.
const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    if (props.persistent && !value) return
    emit('update:modelValue', value)
  },
})

const mobileDialogProps = useMobileDialogProps(isOpen)
const dialogProps = computed(() => ({
  ...mobileDialogProps.value,
  ...(reducedMotion.value ? { transition: false as const } : {}),
}))

// Reset to the first step each time the dialog opens.
watch(
  () => props.modelValue,
  (open) => {
    if (open) step.value = 1
  }
)

function next(): void {
  if (step.value < steps.length) step.value += 1
}

function prev(): void {
  if (step.value > 1) step.value -= 1
}

function finish(): void {
  emit('finish')
  emit('update:modelValue', false)
  router.push({ name: 'family' })
}

function dismiss(): void {
  emit('update:modelValue', false)
  emit('dismiss')
}
</script>

<template>
  <v-dialog
    v-model="isOpen"
    :persistent="persistent"
    max-width="520"
    :aria-labelledby="`${titleId}-${step}`"
    data-testid="help-dialog"
    v-bind="dialogProps"
  >
    <v-card class="help-card" :class="{ 'help-card--dark': theme.current.value.dark }">
      <v-window v-model="step" :transition="reducedMotion ? false : undefined" :touch="false">
        <v-window-item
          v-for="(stepItem, index) in steps"
          :key="stepItem.title"
          :value="index + 1"
          :transition="reducedMotion ? false : undefined"
          :reverse-transition="reducedMotion ? false : undefined"
        >
          <v-sheet
            class="help-illustration"
            :class="`help-illustration--${stepItem.tint}`"
            aria-hidden="true"
            data-testid="help-dialog-illustration"
          >
            <v-avatar class="help-icon" size="104" rounded="xl">
              <v-icon :icon="stepItem.icon" size="64" />
            </v-avatar>
          </v-sheet>
          <v-card-title :id="`${titleId}-${index + 1}`" class="help-title px-6 pt-6 pb-3">
            {{ stepItem.title }}
          </v-card-title>
          <v-card-text class="help-copy px-6 pt-0 pb-4 text-on-surface-variant">
            {{ stepItem.text }}
          </v-card-text>
        </v-window-item>
      </v-window>

      <v-btn
        v-if="!persistent"
        class="help-close"
        icon="mdi-close"
        variant="text"
        width="44"
        height="44"
        aria-label="Close"
        data-testid="help-dialog-close"
        @click="dismiss"
      />

      <v-sheet color="transparent" class="d-flex justify-center" role="tablist" aria-label="Tour steps" data-testid="help-dialog-progress">
        <v-btn
          v-for="(stepItem, index) in steps"
          :key="stepItem.title"
          class="help-dot"
          :class="{ 'help-dot--active': step === index + 1 }"
          variant="text"
          width="44"
          min-width="44"
          height="44"
          role="tab"
          :aria-selected="step === index + 1"
          :aria-label="`Step ${index + 1} of ${steps.length}: ${stepItem.title}`"
          @click="step = index + 1"
        />
      </v-sheet>

      <v-card-actions class="help-actions pa-6 pt-4" :class="{ 'flex-column align-stretch': xs }">
        <v-btn v-if="!persistent && step < steps.length" variant="text" height="44" data-testid="help-dialog-skip" @click="dismiss">
          Skip
        </v-btn>
        <v-btn v-if="step > 1" variant="text" height="44" data-testid="help-dialog-prev" @click="prev">
          Back
        </v-btn>
        <v-spacer v-if="!xs" />
        <v-btn
          v-if="step < steps.length"
          color="primary"
          variant="flat"
          class="fb-chunky help-primary"
          height="52"
          :block="xs"
          data-testid="help-dialog-next"
          @click="next"
        >
          Next
        </v-btn>
        <v-btn
          v-else
          color="primary"
          variant="flat"
          class="fb-chunky help-primary"
          height="52"
          :block="xs"
          data-testid="help-dialog-finish"
          @click="finish"
        >
          Add my first kid
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.help-card {
  position: relative;
  border-radius: 28px;
  color: rgb(var(--v-theme-on-surface));
  --help-blue-bg: #e3eeff;
  --help-blue-ink: #1d4fa8;
  --help-yellow-bg: #fff2c8;
  --help-yellow-ink: #775500;
  --help-green-bg: #d9f5e7;
  --help-green-ink: #176344;
}

.help-card--dark {
  --help-blue-bg: #253754;
  --help-blue-ink: #8db6ff;
  --help-yellow-bg: #44391c;
  --help-yellow-ink: #f7d975;
  --help-green-bg: #203e32;
  --help-green-ink: #86dfb7;
}

.help-illustration {
  display: grid;
  place-items: center;
  height: 200px;
  background: var(--illustration-bg);
  color: var(--illustration-ink);
}

.help-illustration--blue {
  --illustration-bg: var(--help-blue-bg);
  --illustration-ink: var(--help-blue-ink);
}

.help-illustration--yellow {
  --illustration-bg: var(--help-yellow-bg);
  --illustration-ink: var(--help-yellow-ink);
}

.help-illustration--green {
  --illustration-bg: var(--help-green-bg);
  --illustration-ink: var(--help-green-ink);
}

.help-icon {
  background: rgb(var(--v-theme-surface));
}

.help-close {
  position: absolute;
  top: 12px;
  right: 12px;
}

.help-title {
  white-space: normal;
  text-align: center;
  font-family: var(--fb-font-display, 'Bricolage Grotesque', sans-serif);
  font-size: 30px;
  font-weight: 800;
  line-height: 1.1;
}

.help-copy {
  text-align: center;
  font-size: 16px;
  line-height: 1.5;
}

.help-dot::after {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 8px;
  background: rgb(var(--v-theme-on-surface-variant));
}

.help-dot--active::after {
  width: 24px;
  background: rgb(var(--v-theme-primary));
}

.help-actions .help-primary {
  border-radius: 18px;
  font-size: 17px;
  font-weight: 900;
}

.help-card :deep(.v-btn:focus-visible) {
  outline: 3px solid rgb(var(--v-theme-on-surface));
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .help-card :deep(*) {
    animation: none !important;
    transition-property: opacity !important;
    transition-duration: 150ms !important;
  }

  .help-primary:active {
    transform: none !important;
  }
}
</style>
